import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileVideo, RefreshCw, Upload, X } from 'lucide-react';
import {
  api,
  type VsImportPreview,
  type VsImportMatchedRow,
  type VsImportUnmatchedRow,
  type VsImportVideoJob,
} from '@/lib/api';
import { DAY_LABELS, formatMillionsOneDecimal } from '@/lib/vsFormat';

interface Props {
  vsId: number;
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  initialJobId?: string | null;
}

type Resolution =
  | { kind: 'matched'; ocrName: string; row: VsImportMatchedRow; included: boolean }
  | {
      kind: 'unmatched';
      ocrName: string;
      row: VsImportUnmatchedRow;
      playerId: number | null;
      included: boolean;
    };

export default function ImportVsDialog({
  vsId,
  open,
  onClose,
  onImported,
  initialJobId = null,
}: Props) {
  const qc = useQueryClient();
  const [videoFile, setVideoFile] = useState<File | null>(null);
  // Weekly totals are now derived from daily scores — only daily import is offered.
  const kind = 'daily' as const;
  // null → auto-detect. 0..5 → forced Mon..Sat.
  const [dayOverride, setDayOverride] = useState<number | null>(null);
  const [jobId, setJobId] = useState<string | null>(initialJobId);
  const [preview, setPreview] = useState<VsImportPreview | null>(null);
  const [resolutions, setResolutions] = useState<Resolution[]>([]);
  const [needsKindPick, setNeedsKindPick] = useState<VsImportVideoJob['extraction'] | null>(null);

  const jobQuery = useQuery({
    queryKey: ['vs-import-job', vsId, jobId],
    queryFn: () => api.getVsImportVideoJob(vsId, jobId!),
    enabled: !!jobId && !preview,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s && (s === 'done' || s === 'failed' || s === 'needs_kind_pick') ? false : 1500;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!videoFile) throw new Error('No video');
      return api.uploadVsImportVideo(vsId, videoFile, kind, kind === 'daily' ? dayOverride : null);
    },
    onSuccess: (data) => setJobId(data.jobId),
  });

  const previewMutation = useMutation({
    mutationFn: (payload: {
      kind: 'weekly' | 'daily';
      dayIndex: number | null;
      rows: Array<{ name: string; scoreTenthsM: number }>;
    }) => api.previewVsImport(vsId, payload),
    onSuccess: (data) => {
      setPreview(data);
      setResolutions([
        ...data.matched.map<Resolution>((row) => ({
          kind: 'matched',
          ocrName: row.ocrName,
          row,
          included: true,
        })),
        ...data.unmatched.map<Resolution>((row) => ({
          kind: 'unmatched',
          ocrName: row.ocrName,
          row,
          playerId: row.suggestions[0]?.playerId ?? null,
          included: false,
        })),
      ]);
      setNeedsKindPick(null);
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => api.retryVsImportJob(vsId, jobId!),
    onSuccess: (data) => qc.setQueryData(['vs-import-job', vsId, jobId], data),
  });

  const confirmMutation = useMutation({
    mutationFn: async (rows: Array<{ playerId: number; scoreTenthsM: number }>) => {
      if (!preview) throw new Error('No preview');
      // Include the job id so the server can stamp appliedAt on it. We
      // send it via header to keep the confirm body unchanged.
      const headers = jobId ? { 'x-vs-import-job-id': jobId } : undefined;
      const res = await fetch(`/api/vs/${vsId}/import-video/confirm`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(headers ?? {}) },
        body: JSON.stringify({
          kind: preview.kind,
          dayIndex: preview.dayIndex,
          rows,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vs', vsId] });
      qc.invalidateQueries({ queryKey: ['vs'] });
      qc.invalidateQueries({ queryKey: ['vs-import-jobs', vsId] });
      onImported();
      reset();
      onClose();
    },
  });

  // When the job completes: either park in needs_kind_pick, or run preview.
  useEffect(() => {
    const j = jobQuery.data;
    if (!j || preview) return;
    if (j.status === 'needs_kind_pick' && j.extraction) {
      setNeedsKindPick(j.extraction);
      return;
    }
    if (j.status === 'done' && j.extraction) {
      const detectedDay =
        j.kind === 'daily'
          ? (j.requestedDayIndex ?? j.extraction.detected_day_index)
          : null;
      previewMutation.mutate({
        kind: j.kind,
        dayIndex: detectedDay,
        rows: j.extraction.rows,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobQuery.data?.status]);

  function reset() {
    setVideoFile(null);
    setDayOverride(null);
    setJobId(null);
    setPreview(null);
    setResolutions([]);
    setNeedsKindPick(null);
    uploadMutation.reset();
    previewMutation.reset();
    confirmMutation.reset();
    retryMutation.reset();
  }

  function pickKindFromFallback(chosen: { kind: 'weekly' | 'daily'; dayIndex: number | null }) {
    if (!needsKindPick) return;
    previewMutation.mutate({
      kind: chosen.kind,
      dayIndex: chosen.dayIndex,
      rows: needsKindPick.rows,
    });
  }

  const included = useMemo(
    () =>
      resolutions
        .filter((r) => r.included)
        .map((r) => ({
          playerId: r.kind === 'matched' ? r.row.player.id : r.playerId,
          scoreTenthsM:
            r.kind === 'matched' ? r.row.scoreTenthsM : r.row.scoreTenthsM,
        }))
        .filter((r): r is { playerId: number; scoreTenthsM: number } => r.playerId != null),
    [resolutions],
  );

  if (!open) return null;

  const stageText = (() => {
    const j = jobQuery.data;
    if (!j) return 'Uploading…';
    if (j.status === 'failed') return `Failed: ${j.error ?? 'unknown'}`;
    if (j.stage === 'uploading') return 'Uploading video…';
    if (j.stage === 'processing') return 'Extracting VS scores…';
    return 'Working…';
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-3xl rounded-lg border border-slate-800 bg-slate-900 p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Import VS from video</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Step 1: pick file + kind */}
        {!jobId && !preview && !needsKindPick && (
          <div className="mt-4 space-y-4">
            <div className="rounded border border-slate-800 bg-slate-950/40 px-3 py-2 text-xs text-slate-400">
              Daily import — weekly totals are computed automatically from the
              six daily scores.
            </div>

            <label
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
              }}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f && f.type.startsWith('video/')) setVideoFile(f);
              }}
              className="flex cursor-pointer flex-col gap-2 rounded border border-dashed border-slate-700 bg-slate-950/50 p-4 text-sm text-slate-300 hover:border-slate-500"
            >
              <span className="inline-flex items-center gap-2 text-slate-200">
                <FileVideo size={16} />
                {videoFile ? videoFile.name : 'Drop or select .mp4 / .mov'}
              </span>
              <input
                type="file"
                accept="video/mp4,video/quicktime"
                className="hidden"
                onChange={(e) => setVideoFile(e.target.files?.[0] ?? null)}
              />
              <span className="text-xs text-slate-500">Pan slowly over the ranking.</span>
            </label>

            {kind === 'daily' && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-slate-400">Day tab:</span>
                <button
                  onClick={() => setDayOverride(null)}
                  className={dayPickerClass(dayOverride === null)}
                >
                  Auto-detect
                </button>
                {DAY_LABELS.map((d, i) => (
                  <button
                    key={d}
                    onClick={() => setDayOverride(i)}
                    className={dayPickerClass(dayOverride === i)}
                  >
                    {d}
                  </button>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800">
                Cancel
              </button>
              <button
                onClick={() => uploadMutation.mutate()}
                disabled={!videoFile || uploadMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
              >
                <Upload size={14} /> Process
              </button>
            </div>
            {uploadMutation.isError && (
              <div className="text-sm text-red-400">
                {(uploadMutation.error as Error)?.message ?? 'Upload failed'}
              </div>
            )}
          </div>
        )}

        {/* Step 2: job progress */}
        {jobId && !preview && !needsKindPick && (
          <div className="mt-6 space-y-3">
            {jobQuery.data?.status === 'failed' ? (
              <div className="space-y-2 rounded border border-red-800 bg-red-950/40 p-3 text-sm text-red-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="mt-0.5" />
                  <div>
                    <div className="font-medium">Gemini extraction failed</div>
                    <div className="mt-1 text-xs text-red-300/80">
                      {jobQuery.data?.error ?? 'unknown error'}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => retryMutation.mutate()}
                    disabled={retryMutation.isPending || !jobQuery.data?.videoAvailable}
                    className="inline-flex items-center gap-1.5 rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
                  >
                    <RefreshCw size={14} /> Retry
                  </button>
                  <button
                    onClick={() => reset()}
                    className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
                  >
                    Upload a different video
                  </button>
                </div>
                {!jobQuery.data?.videoAvailable && (
                  <div className="text-xs text-red-300/70">
                    Stored video is no longer on disk — re-upload to retry.
                  </div>
                )}
                {retryMutation.isError && (
                  <div className="text-xs text-red-300">
                    {(retryMutation.error as Error)?.message ?? 'Retry failed'}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 text-sm text-slate-300">
                  <div className="h-2 flex-1 overflow-hidden rounded bg-slate-800">
                    <div
                      className="h-full bg-indigo-500 transition-all"
                      style={{
                        width:
                          jobQuery.data?.stage === 'processing'
                            ? '80%'
                            : jobQuery.data?.stage === 'uploading'
                              ? '40%'
                              : '10%',
                      }}
                    />
                  </div>
                  <span>{stageText}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Runs in the background — you can close this dialog and check back later.</span>
                  <button
                    onClick={onClose}
                    className="rounded border border-slate-700 px-3 py-1 text-sm text-slate-300 hover:bg-slate-800"
                  >
                    Check later
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 2b: needs_kind_pick fallback */}
        {needsKindPick && !preview && (
          <div className="mt-4 space-y-3">
            <div className="flex items-start gap-2 rounded border border-amber-700/50 bg-amber-950/30 p-3 text-sm text-amber-200">
              <AlertTriangle size={16} className="mt-0.5" />
              <span>
                Couldn't tell which view this recording shows. Which one is it?
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {DAY_LABELS.map((d, i) => (
                <button
                  key={d}
                  onClick={() => pickKindFromFallback({ kind: 'daily', dayIndex: i })}
                  className="rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm hover:bg-slate-700"
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: preview */}
        {preview && (
          <>
            <PreviewView
              preview={preview}
              resolutions={resolutions}
              setResolutions={setResolutions}
              included={included}
              onCancel={() => reset()}
              onConfirm={() => confirmMutation.mutate(included)}
              confirming={confirmMutation.isPending}
            />
            {confirmMutation.isError && (
              <div className="mt-2 rounded border border-red-800 bg-red-950/40 p-2 text-sm text-red-200">
                {(confirmMutation.error as Error)?.message ?? 'Import failed'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PreviewView({
  preview,
  resolutions,
  setResolutions,
  included,
  onCancel,
  onConfirm,
  confirming,
}: {
  preview: VsImportPreview;
  resolutions: Resolution[];
  setResolutions: (r: Resolution[]) => void;
  included: Array<{ playerId: number; scoreTenthsM: number }>;
  onCancel: () => void;
  onConfirm: () => void;
  confirming: boolean;
}) {
  function toggle(ocrName: string) {
    setResolutions(
      resolutions.map((r) =>
        r.ocrName === ocrName ? { ...r, included: !r.included } : r,
      ),
    );
  }
  function setPlayer(ocrName: string, playerId: number | null) {
    setResolutions(
      resolutions.map((r) =>
        r.kind === 'unmatched' && r.ocrName === ocrName
          ? { ...r, playerId, included: playerId != null }
          : r,
      ),
    );
  }

  const kindLabel =
    preview.kind === 'weekly'
      ? 'Weekly total'
      : `Daily · ${preview.dayIndex != null ? DAY_LABELS[preview.dayIndex] : '—'}`;

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-300">
          <b className="text-white">{kindLabel}</b> · {preview.matched.length} matched ·{' '}
          {preview.unmatched.length} unmatched
        </span>
        <span className="text-slate-500">{included.length} rows will be imported</span>
      </div>

      <div className="max-h-96 overflow-y-auto rounded border border-slate-800">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-950/80 text-xs text-slate-500">
            <tr>
              <th className="w-8 px-2 py-2 text-left"></th>
              <th className="px-2 py-2 text-left">From video</th>
              <th className="px-2 py-2 text-left">Player</th>
              <th className="px-2 py-2 text-right">Score (M)</th>
            </tr>
          </thead>
          <tbody>
            {resolutions.map((r) => (
              <tr key={r.ocrName} className="border-t border-slate-800/70">
                <td className="px-2 py-1.5">
                  <input
                    type="checkbox"
                    checked={r.included}
                    onChange={() => toggle(r.ocrName)}
                    disabled={r.kind === 'unmatched' && r.playerId == null}
                  />
                </td>
                <td className="px-2 py-1.5 text-slate-300">{r.ocrName}</td>
                <td className="px-2 py-1.5">
                  {r.kind === 'matched' ? (
                    <span className="inline-flex items-center gap-1.5 text-slate-200">
                      <CheckCircle2 size={12} className="text-emerald-500" />
                      {r.row.player.displayName ?? r.row.player.name}
                    </span>
                  ) : (
                    <select
                      value={r.playerId ?? ''}
                      onChange={(e) =>
                        setPlayer(r.ocrName, e.target.value ? Number(e.target.value) : null)
                      }
                      className="rounded border border-slate-700 bg-slate-950 px-1.5 py-1 text-xs text-slate-100"
                    >
                      <option value="">— skip —</option>
                      {r.row.suggestions.map((s) => (
                        <option key={s.playerId} value={s.playerId}>
                          {s.displayName ?? s.name} ({Math.round(s.score * 100)}%)
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">
                  {formatMillionsOneDecimal(
                    r.kind === 'matched' ? r.row.scoreTenthsM : r.row.scoreTenthsM,
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={confirming || included.length === 0}
          className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-40"
        >
          Import {included.length} rows
        </button>
      </div>
    </div>
  );
}

function KindTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'rounded border px-3 py-1.5 text-sm ' +
        (active
          ? 'border-indigo-500 bg-indigo-900/40 text-white'
          : 'border-slate-700 text-slate-300 hover:bg-slate-800')
      }
    >
      {children}
    </button>
  );
}

function dayPickerClass(active: boolean) {
  return (
    'rounded border px-2 py-1 text-xs ' +
    (active
      ? 'border-indigo-500 bg-indigo-900/40 text-white'
      : 'border-slate-700 text-slate-300 hover:bg-slate-800')
  );
}
